import { AIRCRAFT_TYPES, aircraftType } from '../data/aircraft';
import { AIRPORTS, airportById } from '../data/airports';
import type { ActiveMarketEvent, AircraftAsset, Airport, GameEvent, GameState, MarketEventCategory, MarketEventResponseOption, Route } from '../game/types';
import { hashFloat } from './math';

export interface GeneratedMarketEvent {
  event: GameEvent;
  active: ActiveMarketEvent;
}

export const MARKET_EVENT_FIRST_WEEK = 5;
export const MARKET_EVENT_INTERVAL_WEEKS = 5;

export function shouldCreateMarketEvent(week: number): boolean {
  return week >= MARKET_EVENT_FIRST_WEEK && (week - MARKET_EVENT_FIRST_WEEK) % MARKET_EVENT_INTERVAL_WEEKS === 0;
}

function pick<T>(items: T[], seed: number, week: number, salt: number): T {
  return items[Math.floor(hashFloat(seed, week, salt) * items.length) % items.length];
}

function routeLabel(route: Route): string {
  return `${route.originId}-${route.destinationId}`;
}

function cityLabel(airportId: string): string {
  const airport = airportById(airportId);
  return `${airport.nameZh} ${airport.iata}`;
}

function connectedAirports(state: GameState): Airport[] {
  const ids = new Set<string>([
    state.headquartersId,
    ...state.hubs.map((hub) => hub.airportId),
    ...state.routes.flatMap((route) => [route.originId, route.destinationId]),
  ]);
  return [...ids].map(airportById);
}

function activeRoutes(state: GameState): Route[] {
  return state.routes.filter((route) => route.active);
}

function eventMix(state: GameState): MarketEventCategory {
  const roll = hashFloat(state.seed, state.week, state.routes.length + state.aircraft.length + 401);
  const early = state.week < 32 || state.routes.length < 4;
  if (early) {
    if (roll < 0.7) return 'demand';
    if (roll < 0.9) return hashFloat(state.seed, state.week, 402) < 0.5 ? 'cost' : 'competition';
    return 'risk';
  }
  if (roll < 0.55) return 'demand';
  if (roll < 0.85) return hashFloat(state.seed, state.week, 403) < 0.5 ? 'cost' : 'competition';
  return 'risk';
}

function responseOptions(event: GameEvent, effect: ActiveMarketEvent['effect']): MarketEventResponseOption[] {
  const multiplier = effect.multiplier;
  const stabilized = multiplier + (1 - multiplier) * 0.55;
  const strengthened = multiplier >= 1 ? multiplier * 1.08 : Math.min(1, multiplier + (1 - multiplier) * 0.35);
  const actionCost = event.tone === 'negative' ? 280_000 : 420_000;
  return [
    {
      id: 'stabilize',
      label: event.tone === 'negative' ? '投入资源缓冲' : '追加运力放大',
      description: event.tone === 'negative' ? '支付现金，将影响拉回中性，保留主要经营节奏。' : '支付现金放大窗口，换取一周更长的机会期。',
      cashCost: actionCost,
      effectPatch: { multiplier: stabilized },
      durationDelta: event.tone === 'negative' ? 0 : 1,
      reputationDelta: event.tone === 'negative' ? 1 : 0,
    },
    {
      id: 'hold',
      label: event.tone === 'negative' ? '接受影响' : '维持当前计划',
      description: event.tone === 'negative' ? '不花现金，承受事件原始影响。' : '不额外投入，按原计划经营。',
      cashCost: 0,
      effectPatch: {},
      durationDelta: 0,
      reputationDelta: 0,
    },
    {
      id: 'shorten',
      label: event.tone === 'negative' ? '削减暴露面' : '快速兑现窗口',
      description: event.tone === 'negative' ? '压缩事件持续时间，但短期会牺牲部分网络表现。' : '减少窗口持续时间，尽快回到常态经营。',
      cashCost: 0,
      effectPatch: { multiplier: strengthened },
      durationDelta: -1,
      reputationDelta: event.tone === 'negative' ? 0 : -1,
    },
  ];
}

function makeActive(event: GameEvent, effect: ActiveMarketEvent['effect']): ActiveMarketEvent {
  return {
    id: `active-${event.id}`,
    sourceEventId: event.id,
    category: event.category ?? 'demand',
    tone: event.tone,
    title: event.title,
    detail: event.detail,
    response: event.response ?? '调配运力，复盘价格策略与航线收益。',
    targetLabel: event.targetLabel ?? '全网',
    weeksRemaining: event.durationWeeks ?? 2,
    effect,
    options: responseOptions(event, effect),
  };
}

function demandEvent(state: GameState, id: string): GeneratedMarketEvent {
  const routes = activeRoutes(state);
  const variant = hashFloat(state.seed, state.week, 501);
  if (routes.length && variant < 0.38) {
    const route = pick(routes, state.seed, state.week, 502);
    const origin = airportById(route.originId);
    const destination = airportById(route.destinationId);
    const durationWeeks = 2;
    const event: GameEvent = {
      id,
      week: state.week,
      tone: 'positive',
      category: 'demand',
      targetLabel: routeLabel(route),
      routeId: route.id,
      durationWeeks,
      title: `${origin.nameZh}-${destination.nameZh} 商务活动升温`,
      detail: `${routeLabel(route)} 的商务需求短期上升 40%，持续 ${durationWeeks} 周。`,
      response: '适合临时租飞机、增派航班，或把该线执飞机型切到商务策略吃高客单。',
    };
    return { event, active: makeActive(event, { kind: 'routeDemand', routeId: route.id, multiplier: 1.4 }) };
  }

  if (variant < 0.72) {
    const candidates = AIRPORTS.filter((airport) => airport.tourism >= 8);
    const airport = pick(candidates, state.seed, state.week, 503);
    const durationWeeks = 4;
    const event: GameEvent = {
      id,
      week: state.week,
      tone: 'positive',
      category: 'demand',
      targetLabel: cityLabel(airport.iata),
      durationWeeks,
      title: `${airport.nameZh} 旅游预订上涨`,
      detail: `旅游城市相关航线需求上升 25%，持续 ${durationWeeks} 周。`,
      response: '适合开通或加密旅游航线，运力不足时可用租赁飞机补临时班次。',
    };
    return { event, active: makeActive(event, { kind: 'cityDemand', airportId: airport.iata, multiplier: 1.25 }) };
  }

  const candidates = connectedAirports(state).filter((airport) => airport.business >= 7);
  const airport = candidates.length ? pick(candidates, state.seed, state.week, 504) : airportById(state.headquartersId);
  const durationWeeks = 3;
  const event: GameEvent = {
    id,
    week: state.week,
    tone: 'positive',
    category: 'demand',
    targetLabel: cityLabel(airport.iata),
    durationWeeks,
    title: `${airport.nameZh} 新企业园区启用`,
    detail: `${airport.iata} 相关航线商务客比例提升，需求上升 30%，持续 ${durationWeeks} 周。`,
    response: '适合增加商务策略座位，或把空闲飞机调往连接该城市的高收益航线。',
  };
  return { event, active: makeActive(event, { kind: 'cityDemand', airportId: airport.iata, multiplier: 1.3 }) };
}

function costEvent(state: GameState, id: string): GeneratedMarketEvent {
  const variant = hashFloat(state.seed, state.week, 601);
  if (variant < 0.4) {
    const durationWeeks = 2;
    const event: GameEvent = {
      id,
      week: state.week,
      tone: 'negative',
      category: 'cost',
      targetLabel: '燃油',
      durationWeeks,
      title: '燃油现货价格上行',
      detail: `所有航线燃油成本上升 12%，持续 ${durationWeeks} 周。`,
      response: '适合减少低利润高频航班，优先保留载客率高或长航程效率好的航线。',
    };
    return { event, active: makeActive(event, { kind: 'fuelCost', multiplier: 1.12 }) };
  }

  if (variant < 0.75) {
    const airport = pick(connectedAirports(state), state.seed, state.week, 602);
    const durationWeeks = 3;
    const event: GameEvent = {
      id,
      week: state.week,
      tone: 'positive',
      category: 'cost',
      targetLabel: cityLabel(airport.iata),
      durationWeeks,
      title: `${airport.nameZh} 起降费优惠`,
      detail: `${airport.iata} 相关航线起降费下降 20%，持续 ${durationWeeks} 周。`,
      response: '适合临时加密该机场相关航线，或趁窗口测试新市场。',
    };
    return { event, active: makeActive(event, { kind: 'airportCost', airportId: airport.iata, multiplier: 0.8 }) };
  }

  const activeFamilies = [...new Set(state.aircraft.map((asset) => aircraftType(asset.typeId).family))];
  const family = activeFamilies.length ? pick(activeFamilies, state.seed, state.week, 603) : pick(AIRCRAFT_TYPES, state.seed, state.week, 604).family;
  const durationWeeks = 2;
  const event: GameEvent = {
    id,
    week: state.week,
    tone: 'negative',
    category: 'cost',
    targetLabel: family,
    durationWeeks,
    title: `${family} 维修件短缺`,
    detail: `${family} 飞机维护与循环成本上升 15%，持续 ${durationWeeks} 周。`,
    response: '适合把低利润航线换给其他机型，或提前维修状况较差的飞机。',
  };
  return { event, active: makeActive(event, { kind: 'aircraftMaintenance', family, multiplier: 1.15 }) };
}

function competitionEvent(state: GameState, id: string): GeneratedMarketEvent {
  const routes = activeRoutes(state);
  const variant = hashFloat(state.seed, state.week, 701);
  if (routes.length && variant < 0.45) {
    const route = pick(routes, state.seed, state.week, 702);
    const durationWeeks = 3;
    const event: GameEvent = {
      id,
      week: state.week,
      tone: 'negative',
      category: 'competition',
      targetLabel: routeLabel(route),
      routeId: route.id,
      durationWeeks,
      title: `${routeLabel(route)} 竞争对手降价`,
      detail: `该航线票价承压，客票收入下降 8%，持续 ${durationWeeks} 周。`,
      response: '适合切换低价策略保客流，或把飞机转去更有需求缺口的航线。',
    };
    return { event, active: makeActive(event, { kind: 'routeRevenue', routeId: route.id, multiplier: 0.92 }) };
  }

  if (routes.length && variant < 0.75) {
    const route = pick(routes, state.seed, state.week, 703);
    const durationWeeks = 2;
    const event: GameEvent = {
      id,
      week: state.week,
      tone: 'positive',
      category: 'competition',
      targetLabel: routeLabel(route),
      routeId: route.id,
      durationWeeks,
      title: `${routeLabel(route)} 对手退出`,
      detail: `该航线需求外溢上升 30%，持续 ${durationWeeks} 周。`,
      response: '适合立即加班次或临租飞机，窗口短，错过后需求会回落。',
    };
    return { event, active: makeActive(event, { kind: 'routeDemand', routeId: route.id, multiplier: 1.3 }) };
  }

  const airport = pick(connectedAirports(state), state.seed, state.week, 704);
  const durationWeeks = 3;
  const event: GameEvent = {
    id,
    week: state.week,
    tone: 'neutral',
    category: 'competition',
    targetLabel: cityLabel(airport.iata),
    durationWeeks,
    title: `新航司进入 ${airport.nameZh}`,
    detail: `${airport.iata} 相关航线收益率下降 6%，持续 ${durationWeeks} 周。`,
    response: '适合把运力转向更高载客率航线，或用低价策略守住关键市场份额。',
  };
  return { event, active: makeActive(event, { kind: 'cityRevenue', airportId: airport.iata, multiplier: 0.94 }) };
}

interface AircraftCandidate {
  asset: AircraftAsset;
  managerName?: string;
}

function activeAircraftCandidates(state: GameState): AircraftCandidate[] {
  const player = state.aircraft
    .filter((asset) => asset.routeIds.length)
    .map((asset) => ({ asset }));
  const ai = state.aiManagers?.flatMap((manager) => (
    manager.bankrupt
      ? []
      : manager.aircraft
        .filter((asset) => asset.routeIds.length)
        .map((asset) => ({ asset, managerName: manager.name }))
  )) ?? [];
  return [...player, ...ai];
}

function riskEvent(state: GameState, id: string): GeneratedMarketEvent {
  const candidates = activeAircraftCandidates(state);
  const variant = hashFloat(state.seed, state.week, 801);
  if (candidates.length && variant < 0.55) {
    const candidate = pick(candidates, state.seed, state.week, 802);
    const { asset, managerName } = candidate;
    const type = aircraftType(asset.typeId);
    const durationWeeks = 1;
    const ownerPrefix = managerName ? `${managerName} 的 ` : '';
    const event: GameEvent = {
      id,
      week: state.week,
      tone: 'negative',
      category: 'risk',
      targetLabel: managerName ? `${managerName} · ${type.name}` : type.name,
      durationWeeks,
      title: `${ownerPrefix}${type.name} 轻微故障预警`,
      detail: `该飞机下周可飞行小时下降 20%。`,
      response: '适合安排维修、用备用飞机顶班，或暂时把它撤离高需求航线。',
    };
    return { event, active: makeActive(event, { kind: 'aircraftCapacity', aircraftId: asset.id, multiplier: 0.8 }) };
  }

  const airport = pick(connectedAirports(state), state.seed, state.week, 803);
  const durationWeeks = 1 + Math.floor(hashFloat(state.seed, state.week, 804) * 2);
  const event: GameEvent = {
    id,
    week: state.week,
    tone: 'negative',
    category: 'risk',
    targetLabel: cityLabel(airport.iata),
    durationWeeks,
    title: `${airport.nameZh} 天气运行受限`,
    detail: `${airport.iata} 相关航线收入下降 7%，持续 ${durationWeeks} 周。`,
    response: '适合减少边际航班、保留高载客率航线，并准备备用飞机降低服务风险。',
  };
  return { event, active: makeActive(event, { kind: 'cityRevenue', airportId: airport.iata, multiplier: 0.93 }) };
}

export function createWeeklyMarketEvent(state: GameState, id: string): GeneratedMarketEvent | null {
  if (!shouldCreateMarketEvent(state.week)) return null;
  const category = eventMix(state);
  if (category === 'demand') return demandEvent(state, id);
  if (category === 'cost') return costEvent(state, id);
  if (category === 'competition') return competitionEvent(state, id);
  return riskEvent(state, id);
}

type MarketEventState = Partial<Pick<GameState, 'activeMarketEvents'>>;

export function routeDemandMultiplier(route: Route | undefined, origin: Airport, destination: Airport, state: MarketEventState): number {
  return (state.activeMarketEvents ?? []).reduce((multiplier, event) => {
    const effect = event.effect;
    if (effect.kind === 'routeDemand' && route?.id === effect.routeId) return multiplier * effect.multiplier;
    if (effect.kind === 'cityDemand' && (origin.iata === effect.airportId || destination.iata === effect.airportId)) return multiplier * effect.multiplier;
    if (effect.kind === 'tourismDemand' && (origin.tourism >= 8 || destination.tourism >= 8)) return multiplier * effect.multiplier;
    return multiplier;
  }, 1);
}

export function routeRevenueMultiplier(route: Route | undefined, origin: Airport, destination: Airport, state: MarketEventState): number {
  return (state.activeMarketEvents ?? []).reduce((multiplier, event) => {
    const effect = event.effect;
    if (effect.kind === 'routeRevenue' && route?.id === effect.routeId) return multiplier * effect.multiplier;
    if (effect.kind === 'cityRevenue' && (origin.iata === effect.airportId || destination.iata === effect.airportId)) return multiplier * effect.multiplier;
    return multiplier;
  }, 1);
}

export function airportCostMultiplier(originId: string, destinationId: string, state: MarketEventState): number {
  return (state.activeMarketEvents ?? []).reduce((multiplier, event) => {
    const effect = event.effect;
    if (effect.kind === 'airportCost' && (originId === effect.airportId || destinationId === effect.airportId)) return multiplier * effect.multiplier;
    return multiplier;
  }, 1);
}

export function fuelCostMultiplier(state: MarketEventState): number {
  return (state.activeMarketEvents ?? []).reduce((multiplier, event) => {
    const effect = event.effect;
    if (effect.kind === 'fuelCost') return multiplier * effect.multiplier;
    return multiplier;
  }, 1);
}

export function applyAircraftMarketEventCosts<T extends { roundTrips: number; seats: number; flightHours: number; fuelGallons: number; ownershipCost: number; cycleCost: number; fixedCost: number; flightCost: number }>(
  asset: AircraftAsset,
  operation: T,
  state: MarketEventState,
): T {
  const next = { ...operation };
  for (const event of state.activeMarketEvents ?? []) {
    const effect = event.effect;
    if (effect.kind === 'aircraftCapacity' && effect.aircraftId === asset.id) {
      next.roundTrips = Math.max(0, Math.floor(next.roundTrips * effect.multiplier));
      next.seats = Math.max(0, Math.floor(next.seats * effect.multiplier));
      next.flightHours *= effect.multiplier;
      next.fuelGallons *= effect.multiplier;
      next.flightCost *= effect.multiplier;
      next.cycleCost *= effect.multiplier;
    }
    if (effect.kind === 'aircraftMaintenance' && aircraftType(asset.typeId).family === effect.family) {
      next.ownershipCost *= effect.multiplier;
      next.cycleCost *= effect.multiplier;
    }
  }
  next.fixedCost = next.ownershipCost + next.cycleCost;
  return next;
}

export function decrementMarketEvents(events: ActiveMarketEvent[] = []): ActiveMarketEvent[] {
  return events
    .map((event) => ({ ...event, weeksRemaining: event.weeksRemaining - 1 }))
    .filter((event) => event.weeksRemaining > 0);
}

function effectKey(event: ActiveMarketEvent): string {
  const effect = event.effect;
  switch (effect.kind) {
    case 'routeDemand':
    case 'routeRevenue':
      return `${effect.kind}:${effect.routeId}`;
    case 'cityDemand':
    case 'cityRevenue':
    case 'airportCost':
      return `${effect.kind}:${effect.airportId}`;
    case 'fuelCost':
      return effect.kind;
    case 'aircraftMaintenance':
      return `${effect.kind}:${effect.family}`;
    case 'aircraftCapacity':
      return `${effect.kind}:${effect.aircraftId}`;
    case 'tourismDemand':
      return effect.kind;
  }
}

function isNegativeStack(event: ActiveMarketEvent): boolean {
  const effect = event.effect;
  if (effect.kind === 'fuelCost' || effect.kind === 'airportCost' || effect.kind === 'aircraftMaintenance') return effect.multiplier > 1;
  if (effect.kind === 'routeDemand' || effect.kind === 'cityDemand' || effect.kind === 'tourismDemand' || effect.kind === 'routeRevenue' || effect.kind === 'cityRevenue' || effect.kind === 'aircraftCapacity') return effect.multiplier < 1;
  return false;
}

export function mergeMarketEvents(events: ActiveMarketEvent[]): ActiveMarketEvent[] {
  const merged: ActiveMarketEvent[] = [];
  for (const event of events) {
    if (event.resolvedOptionId) {
      merged.push(event);
      continue;
    }
    const key = effectKey(event);
    const existing = merged.find((item) => !item.resolvedOptionId && effectKey(item) === key && isNegativeStack(item) && isNegativeStack(event));
    if (!existing) {
      merged.push(event);
      continue;
    }
    existing.weeksRemaining = Math.min(8, Math.max(existing.weeksRemaining, event.weeksRemaining) + 1);
    existing.detail = `${existing.detail} 新事件已并入同一影响，持续时间延长。`;
    existing.response = `${existing.response} 当前同类负面效果已合并，避免重复叠加。`;
  }
  return merged.slice(-8);
}
